import type { BacktestMetrics, Candle } from '../../domain/types'
import { mean } from './statistics'
import { drawdown, longestLosingStreak, sharpeLike } from './risk'
import { atr, bollingerBands, detectGaps, rsi, sma } from './indicators'
import { kellyFraction } from './kelly'

export type { BacktestMetrics } from '../../domain/types'

export interface CostModel {
  feePct: number
  slippagePct: number
  spreadPct: number
  commissionPerTrade?: number
}

export const DEFAULT_COSTS: CostModel = { feePct: 0, slippagePct: 0.0005, spreadPct: 0.0005 }

export type StockStrategyType = 'sma-cross' | 'rsi-reversion' | 'momentum' | 'breakout' | 'vol-squeeze' | 'gap-fill'

export interface StockStrategyParams {
  type: StockStrategyType
  fast: number
  slow: number
  rsiPeriod: number
  rsiLow: number
  rsiHigh: number
  momentumPeriod: number
  momentumThreshold: number
  breakoutLookback: number
  breakoutBufferPct: number
  squeezePeriod: number
  squeezeThresholdPct: number
  gapThresholdPct: number
  gapMaxAge: number
  useAtrStop: boolean
  atrPeriod: number
  atrStopMultiple: number
  allowShort: boolean
}

export const DEFAULT_STOCK_PARAMS: StockStrategyParams = {
  type: 'sma-cross',
  fast: 20,
  slow: 50,
  rsiPeriod: 14,
  rsiLow: 30,
  rsiHigh: 70,
  momentumPeriod: 20,
  momentumThreshold: 0.0,
  breakoutLookback: 20,
  breakoutBufferPct: 0.0025,
  squeezePeriod: 20,
  squeezeThresholdPct: 0.08,
  gapThresholdPct: 0.02,
  gapMaxAge: 5,
  useAtrStop: true,
  atrPeriod: 14,
  atrStopMultiple: 2,
  allowShort: false,
}

export function generateSignals(candles: readonly Candle[], params: StockStrategyParams): number[] {
  const closes = candles.map((c) => c.close)
  const n = closes.length
  const signals = new Array<number>(n).fill(0)
  const flat = (s: number) => (params.allowShort ? s : Math.max(0, s))

  if (params.type === 'sma-cross') {
    const fast = sma(closes, params.fast)
    const slow = sma(closes, params.slow)
    for (let i = 0; i < n; i++) {
      const f = fast[i]
      const s = slow[i]
      if (f === null || s === null) continue
      signals[i] = flat(f > s ? 1 : -1)
    }
  } else if (params.type === 'rsi-reversion') {
    const r = rsi(closes, params.rsiPeriod)
    let pos = 0
    for (let i = 0; i < n; i++) {
      const v = r[i]
      if (v !== null) {
        if (v < params.rsiLow) pos = 1
        else if (v > params.rsiHigh) pos = params.allowShort ? -1 : 0
      }
      signals[i] = pos
    }
  } else if (params.type === 'momentum') {
    const p = params.momentumPeriod
    for (let i = 0; i < n; i++) {
      if (i <= p) continue
      const roc = closes[i - p] > 0 ? (closes[i] - closes[i - p]) / closes[i - p] : 0
      signals[i] = flat(roc > params.momentumThreshold ? 1 : roc < -params.momentumThreshold ? -1 : 0)
    }
  } else if (params.type === 'breakout') {
    const lookback = Math.max(2, Math.round(params.breakoutLookback))
    const buffer = Math.max(0, params.breakoutBufferPct)
    let pos = 0
    for (let i = lookback; i < n; i++) {
      const prev = candles.slice(i - lookback, i)
      const high = Math.max(...prev.map((c) => c.high))
      const low = Math.min(...prev.map((c) => c.low))
      if (closes[i] > high * (1 + buffer)) pos = 1
      else if (closes[i] < low * (1 - buffer)) pos = params.allowShort ? -1 : 0
      signals[i] = flat(pos)
    }
  } else if (params.type === 'vol-squeeze') {
    const bands = bollingerBands(closes, Math.max(5, params.squeezePeriod))
    let pos = 0
    for (let i = 1; i < n; i++) {
      const upper = bands.upper[i]
      const lower = bands.lower[i]
      const mid = bands.middle[i]
      const prevUpper = bands.upper[i - 1]
      const prevLower = bands.lower[i - 1]
      const prevMid = bands.middle[i - 1]
      if (upper === null || lower === null || mid === null || prevUpper === null || prevLower === null || prevMid === null || prevMid <= 0) {
        signals[i] = pos
        continue
      }
      const prevWidth = (prevUpper - prevLower) / prevMid
      const wasSqueezed = prevWidth <= params.squeezeThresholdPct
      if (wasSqueezed && closes[i] > upper) pos = 1
      else if (wasSqueezed && closes[i] < lower) pos = params.allowShort ? -1 : 0
      else if (pos === 1 && closes[i] < mid) pos = 0
      else if (pos === -1 && closes[i] > mid) pos = 0
      signals[i] = flat(pos)
    }
  } else if (params.type === 'gap-fill') {
    const gaps = detectGaps(candles, params.gapThresholdPct)
    for (const gap of gaps) {
      const prevClose = candles[gap.index - 1]?.close
      if (!prevClose) continue
      const signal = gap.direction === 'down' ? 1 : params.allowShort ? -1 : 0
      const end = Math.min(n - 1, gap.index + Math.max(1, Math.round(params.gapMaxAge)))
      for (let i = gap.index; i <= end; i++) {
        const filled = gap.direction === 'down' ? candles[i].high >= prevClose : candles[i].low <= prevClose
        signals[i] = filled ? 0 : flat(signal)
        if (filled) break
      }
    }
  }
  return signals
}

export interface SimResult {
  equityCurve: number[]
  tradePnls: number[]
  stepReturns: number[]
  stopouts: number
}

export interface StopModel {
  enabled: boolean
  atrPeriod: number
  atrMultiple: number
}

// Signal at bar i is realised over the next bar's return; turnover pays spread+slippage+fee.
export function simulateSignals(
  candles: readonly Candle[],
  signals: readonly number[],
  costs: CostModel,
  initialCapital = 10000,
  stopModel?: StopModel,
): SimResult {
  const closes = candles.map((c) => c.close)
  const cost = costs.spreadPct + costs.slippagePct + costs.feePct
  const commission = costs.commissionPerTrade ?? 0
  const atrValues = stopModel?.enabled ? atr(candles, stopModel.atrPeriod) : []
  let equity = initialCapital
  const equityCurve: number[] = [equity]
  const stepReturns: number[] = []
  const tradePnls: number[] = []
  let stopouts = 0

  let prevSignal = 0
  let tradeOpenEquity = equity
  let inTrade = false
  let stopPrice: number | null = null

  for (let i = 0; i < closes.length - 1; i++) {
    const signal = signals[i]
    const turnover = Math.abs(signal - prevSignal)
    if (turnover > 0) {
      equity -= equity * cost * turnover
      equity -= commission * turnover
      if (inTrade && (signal === 0 || Math.sign(signal) !== Math.sign(prevSignal))) {
        tradePnls.push(equity - tradeOpenEquity)
        inTrade = false
        stopPrice = null
      }
      if (!inTrade && signal !== 0) {
        tradeOpenEquity = equity
        inTrade = true
        const atrValue = atrValues[i]
        stopPrice = typeof atrValue === 'number' && Number.isFinite(atrValue)
          ? signal > 0
            ? closes[i] - atrValue * (stopModel?.atrMultiple ?? 2)
            : closes[i] + atrValue * (stopModel?.atrMultiple ?? 2)
          : null
      }
    }

    let effectiveSignal = signal
    let exitPrice = closes[i + 1]
    if (inTrade && stopModel?.enabled && stopPrice !== null && signal !== 0) {
      const atrValue = atrValues[i]
      if (typeof atrValue === 'number' && Number.isFinite(atrValue)) {
        stopPrice = signal > 0
          ? Math.max(stopPrice, closes[i] - atrValue * stopModel.atrMultiple)
          : Math.min(stopPrice, closes[i] + atrValue * stopModel.atrMultiple)
      }
      const next = candles[i + 1]
      const stoppedLong = signal > 0 && next.low <= stopPrice
      const stoppedShort = signal < 0 && next.high >= stopPrice
      if (stoppedLong || stoppedShort) {
        exitPrice = stopPrice
        effectiveSignal = signal
        stopouts++
      }
    }
    if (inTrade && stopModel?.enabled && stopPrice === null && signal !== 0) {
      const atrValue = atrValues[i]
      if (typeof atrValue === 'number' && Number.isFinite(atrValue)) {
        stopPrice = signal > 0
          ? closes[i] - atrValue * stopModel.atrMultiple
          : closes[i] + atrValue * stopModel.atrMultiple
      }
    }

    const barReturn = closes[i] > 0 ? (exitPrice - closes[i]) / closes[i] : 0
    const stratReturn = effectiveSignal * barReturn
    equity *= 1 + stratReturn
    stepReturns.push(stratReturn)
    if (exitPrice !== closes[i + 1] && inTrade) {
      equity -= commission
      tradePnls.push(equity - tradeOpenEquity)
      inTrade = false
      stopPrice = null
      prevSignal = 0
    } else {
      prevSignal = signal
    }
    equityCurve.push(equity)
  }

  if (inTrade) tradePnls.push(equity - tradeOpenEquity)
  return { equityCurve, tradePnls, stepReturns, stopouts }
}

export function summarize(
  equityCurve: readonly number[],
  tradePnls: readonly number[],
  stepReturns: readonly number[],
): BacktestMetrics {
  const first = equityCurve[0] ?? 0
  const last = equityCurve[equityCurve.length - 1] ?? 0
  const wins = tradePnls.filter((p) => p > 0)
  const losses = tradePnls.filter((p) => p < 0)
  const grossWin = wins.reduce((a, b) => a + b, 0)
  const grossLoss = Math.abs(losses.reduce((a, b) => a + b, 0))
  const dd = drawdown(equityCurve as number[])

  return {
    totalReturnPct: first > 0 ? (last / first - 1) * 100 : 0,
    maxDrawdownPct: dd.maxDrawdownPct * 100,
    sharpeLike: sharpeLike(stepReturns),
    winRate: tradePnls.length ? wins.length / tradePnls.length : 0,
    avgWin: wins.length ? mean(wins) : 0,
    avgLoss: losses.length ? mean(losses) : 0,
    expectancy: tradePnls.length ? mean(tradePnls as number[]) : 0,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0,
    longestLosingStreak: longestLosingStreak(tradePnls),
    trades: tradePnls.length,
  }
}

export interface StockBacktestResult {
  metrics: BacktestMetrics
  inSample: BacktestMetrics
  outOfSample: BacktestMetrics
  equityCurve: number[]
  overfitWarning?: string
  walkForward: WalkForwardSegment[]
  signals: number[]
  stopouts: number
}

export interface WalkForwardSegment {
  label: string
  returnPct: number
  maxDrawdownPct: number
  trades: number
}

export function backtestStock(
  candles: readonly Candle[],
  params: StockStrategyParams,
  costs: CostModel = DEFAULT_COSTS,
  initialCapital = 10000,
  splitRatio = 0.7,
): StockBacktestResult {
  const signals = generateSignals(candles, params)
  const stopModel = params.useAtrStop ? { enabled: true, atrPeriod: params.atrPeriod, atrMultiple: params.atrStopMultiple } : undefined
  const full = simulateSignals(candles, signals, costs, initialCapital, stopModel)
  const metrics = summarize(full.equityCurve, full.tradePnls, full.stepReturns)

  const splitIdx = Math.floor(candles.length * splitRatio)
  const inCandles = candles.slice(0, splitIdx)
  const outCandles = candles.slice(splitIdx)
  const inSim = simulateSignals(inCandles, generateSignals(inCandles, params), costs, initialCapital, stopModel)
  const outSim = simulateSignals(outCandles, generateSignals(outCandles, params), costs, initialCapital, stopModel)
  const inSample = summarize(inSim.equityCurve, inSim.tradePnls, inSim.stepReturns)
  const outOfSample = summarize(outSim.equityCurve, outSim.tradePnls, outSim.stepReturns)

  return {
    metrics,
    inSample,
    outOfSample,
    equityCurve: full.equityCurve,
    overfitWarning: overfitWarning(inSample, outOfSample),
    walkForward: walkForward(candles, params, costs, initialCapital),
    signals,
    stopouts: full.stopouts,
  }
}

export function overfitWarning(inSample: BacktestMetrics, outOfSample: BacktestMetrics): string | undefined {
  if (inSample.trades < 5 || outOfSample.trades < 3) {
    return 'Too few trades to trust these results. Treat as noise, not signal.'
  }
  if (inSample.totalReturnPct > 0 && outOfSample.totalReturnPct <= 0) {
    return 'Out-of-sample return is negative while in-sample is positive — classic overfitting. Do not deploy.'
  }
  if (outOfSample.totalReturnPct < inSample.totalReturnPct * 0.4) {
    return 'Performance decays sharply out-of-sample. The edge may be curve-fit to the past.'
  }
  return undefined
}

export function walkForward(
  candles: readonly Candle[],
  params: StockStrategyParams,
  costs: CostModel,
  initialCapital: number,
  segments = 4,
): WalkForwardSegment[] {
  const out: WalkForwardSegment[] = []
  const size = Math.floor(candles.length / segments)
  if (size < 5) return out
  for (let s = 0; s < segments; s++) {
    const start = s * size
    const end = s === segments - 1 ? candles.length : start + size
    const slice = candles.slice(start, end)
    const stopModel = params.useAtrStop ? { enabled: true, atrPeriod: params.atrPeriod, atrMultiple: params.atrStopMultiple } : undefined
    const sim = simulateSignals(slice, generateSignals(slice, params), costs, initialCapital, stopModel)
    const m = summarize(sim.equityCurve, sim.tradePnls, sim.stepReturns)
    out.push({
      label: `Segment ${s + 1}`,
      returnPct: m.totalReturnPct,
      maxDrawdownPct: m.maxDrawdownPct,
      trades: m.trades,
    })
  }
  return out
}

export interface HistoricalBet {
  prob: number
  decimalOdds: number
  won: boolean
  closingDecimal?: number
}

export interface BetBacktestParams {
  stakeFraction: number
  flat: boolean
  feePct: number
  maxStakePct: number
}

export interface BetBacktestResult {
  metrics: BacktestMetrics
  equityCurve: number[]
  avgClvPct: number
  endingBankroll: number
}

export function backtestBets(
  bets: readonly HistoricalBet[],
  params: BetBacktestParams,
  initialCapital = 10000,
): BetBacktestResult {
  let bankroll = initialCapital
  const equityCurve: number[] = [bankroll]
  const tradePnls: number[] = []
  const stepReturns: number[] = []
  let clvSum = 0
  let clvCount = 0

  for (const bet of bets) {
    const kelly = params.flat ? params.stakeFraction : kellyFraction(bet.prob, bet.decimalOdds) * params.stakeFraction
    const fraction = Math.min(kelly, params.maxStakePct)
    const stake = Math.max(0, bankroll * fraction)
    const before = bankroll
    if (bet.won) bankroll += stake * (bet.decimalOdds - 1) * (1 - params.feePct)
    else bankroll -= stake
    const pnl = bankroll - before
    tradePnls.push(pnl)
    stepReturns.push(before > 0 ? pnl / before : 0)
    equityCurve.push(bankroll)
    if (bet.closingDecimal && bet.closingDecimal > 1) {
      clvSum += (bet.decimalOdds / bet.closingDecimal - 1) * 100
      clvCount++
    }
  }

  return {
    metrics: summarize(equityCurve, tradePnls, stepReturns),
    equityCurve,
    avgClvPct: clvCount ? clvSum / clvCount : 0,
    endingBankroll: bankroll,
  }
}
